import { User } from "@/types/users"
import { getServerOrClientCookies } from "./cookies"
import { getLoggedInUser } from "../api/users"

/**
 * Helper function to get the wallet address of a user.
 * Returns the wallet address as a 0x-prefixed string, or null if not found.
 */
export function getWalletAddress(user: User): `0x${string}` | null {
  if ("wallet_address" in user) {
    return user.wallet_address as `0x${string}`
  }

  if ("lowercase_wallet_address" in user) {
    return user.lowercase_wallet_address as `0x${string}`
  }

  return null
}

/**
 * Whether the user is logged in or not.
 *
 * This doesn't actually verify the validity of the cookie, so only use this to render UI
 * for logged-out users.
 *
 * For example, rather than trying to fetch data for a logged-in user in a server component,
 * and then redirecting if the user isn't logged in (e.g. their JWT expired), you can use this
 * function to render a logged-out UI for the user if we know they don't have any cookies and
 * they're trying to access a public route.
 *
 * Can be called from a server component or a client component.
 */
export async function isUserLoggedIn() {
  const jwt = await getServerOrClientCookies("jwt")

  return jwt !== undefined && jwt !== ""
}

/**
 * Get the user if they're logged in.
 *
 * Can be called from a server component or a client component.
 */
export async function getUser() {
  // Fetch the logged in user, if any.
  const isLoggedIn = await isUserLoggedIn()

  // We don't want to fetch the user if they're not logged in as that would
  // redirect any logged out users to the login page.
  const user = isLoggedIn ? await getLoggedInUser() : null

  if (!isLoggedIn || !user) {
    return null
  }

  return user
}
